import * as THREE from 'three';
import { BLACK, std } from '../materials.ts';
import { M, V, glowSprite, limb } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * El Ahogado (La Orilla, docs/SAGAS.md): a nightmare shaped like a fisherman the sea kept. Swollen pale grey-green
 * flesh, a barrel of a belly straining an old oilskin, short heavy legs, thick arms with puffy hands; a bloated head
 * with sunken ink-black eyes under a sodden cap. Old nets hang tangled over its shoulders and trail behind, and
 * stones are tied on with rope (at the belt, at a wrist), the way the drowned were weighed down. It drips. Slow and
 * heavy: the troll's skeleton, so it moves with the troll's clips (its stoop and its dragging arms fit the weight).
 */


export const drowned = (o?: BaseOpts): Model => {
  const c = begin(o, 0.8, 83);
  const { g, R } = c;
  const flesh = std('#8a9a88', { roughness: 0.42, metalness: 0.05, bumpScale: 0.05 });
  const fleshD = std('#6a7a6a', { roughness: 0.5, bumpScale: 0.06 });
  const bloat = std('#a4b2a0', { roughness: 0.32, metalness: 0.06 });
  const oil = std('#3a4c44', { roughness: 0.35, metalness: 0.15, bumpScale: 0.05 });
  const oilD = std('#26322e', { roughness: 0.45, bumpScale: 0.05 });
  const net = std('#4a5040', { roughness: 0.9, transparent: true, opacity: 0.85, side: THREE.DoubleSide });
  const rope = std('#5a4a36', { roughness: 0.95 });
  const stone = std('#4a4c52', { roughness: 0.85, flatShading: true });
  const weed = std('#2f4a2a', { roughness: 0.9, side: THREE.DoubleSide });
  const water = std('#9fc4d0', { roughness: 0.05, metalness: 0.3, transparent: true, opacity: 0.55, depthWrite: false });

  // The troll's skeleton: the same bones, so its clips fit (a little shorter and much wider).
  const SH = [-1, 1].map((k) => V(k * 0.46, 1.62, 0.2));
  const EL = [-1, 1].map((k) => V(k * 0.62, 1.12, 0.3));
  const WR = [-1, 1].map((k) => V(k * 0.6, 0.62, 0.42));
  const hipsJ = joint(g, V(0, 0.95, -0.04), 'hips');
  const chestJ = joint(hipsJ.abs, V(0, 1.1, -0.02), 'chest');
  const ch = chestJ.abs;
  const headJ = joint(ch, V(0, 1.78, 0.3), 'head');
  const jawJ = joint(headJ.abs, V(0, 1.72, 0.44), 'jaw');
  const maneJ = joint(ch, V(0, 1.86, 0.08), 'mane');
  const armJ = [-1, 1].map((k, i) => joint(ch, SH[i]!, k < 0 ? 'armL' : 'armR'));
  const foreJ = [-1, 1].map((k, i) => joint(armJ[i]!.abs, EL[i]!, k < 0 ? 'foreL' : 'foreR'));
  const handJ = [-1, 1].map((k, i) => joint(foreJ[i]!.abs, WR[i]!, k < 0 ? 'handL' : 'handR'));
  const legJ = [-1, 1].map((k) => joint(hipsJ.abs, V(k * 0.24, 0.92, -0.04), k < 0 ? 'legL' : 'legR'));
  const KNEE = [-1, 1].map((k) => V(k * 0.28, 0.5, 0.08));
  const shinJ = [-1, 1].map((k, i) => joint(legJ[i]!.abs, KNEE[i]!, k < 0 ? 'shinL' : 'shinR'));

  const lump = (parent: THREE.Object3D, mat: THREE.Material, r: number, x: number, y: number, z: number, sx = 1, sy = 1, sz = 1, seg = 10): THREE.Mesh => {
    const m = M(new THREE.SphereGeometry(r, seg, Math.max(6, seg - 3)), mat, parent);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    return m;
  };
  const tube = (parent: THREE.Object3D, pts: THREE.Vector3[], r: number, mat: THREE.Material, seg = pts.length * 2): THREE.Mesh =>
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.3), seg, r, 4), mat, parent);
  /** A stone tied on: a rope from `from` down to a lumpy rock. */
  const weight = (parent: THREE.Object3D, from: THREE.Vector3, drop: number, r: number): void => {
    const to = from.clone().add(V(R() * 0.04, -drop, R() * 0.04));
    tube(parent, [from, from.clone().lerp(to, 0.5).add(V(0.02, 0, 0.02)), to], 0.012, rope, 4);
    const s = M(new THREE.DodecahedronGeometry(r, 0), stone, parent);
    s.position.copy(to).add(V(0, -r * 0.8, 0));
    s.rotation.set(R(), R(), R());
    // The rope bound round it.
    const b = M(new THREE.TorusGeometry(r * 0.95, 0.012, 4, 10), rope, parent);
    b.position.copy(s.position);
    b.rotation.x = Math.PI / 2 + R() * 0.4;
  };
  /** A sagging piece of net: a few crossing strands draped between two points. */
  const netting = (parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, sag: number, rows = 4): void => {
    for (let r = 0; r < rows; r++) {
      const k = r / (rows - 1);
      const p0 = a.clone().add(V(0, -k * sag, 0.01 * r));
      const p1 = b.clone().add(V(0, -k * sag, 0.01 * r));
      const mid = p0.clone().lerp(p1, 0.5).add(V(0, -sag * 0.5, 0.04));
      tube(parent, [p0, mid, p1], 0.008, net, 6);
    }
    for (let i = 0; i <= 4; i++) {
      const k = i / 4;
      const top = a.clone().lerp(b, k);
      tube(parent, [top, top.clone().add(V(0, -sag * 0.55, 0.05)), top.clone().add(V(R() * 0.03, -sag * 1.15, 0.03))], 0.008, net, 4);
    }
  };

  // Legs: short, swollen, the boots long gone; dragging weed from the ankles.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const th = legJ[i]!.abs;
    const sh = shinJ[i]!.abs;
    const hip = V(k * 0.24, 0.92, -0.04);
    const knee = KNEE[i]!;
    const ank = V(k * 0.3, 0.1, -0.02);
    limb(th, hip, knee, 0.2, 0.16, oilD, 9);
    lump(th, oil, 0.17, k * 0.27, 0.72, 0.02, 1, 1.4, 1);
    limb(sh, knee, ank, 0.15, 0.12, flesh, 9);
    lump(sh, fleshD, 0.15, k * 0.3, 0.06, 0.06, 1.05, 0.5, 1.5);
    for (let w = 0; w < 2; w++) tube(sh, [V(k * 0.3, 0.12, -0.08), V(k * (0.32 + w * 0.05), 0.05, -0.24), V(k * (0.3 + w * 0.08), 0.01, -0.42)], 0.02, weed, 4);
  }
  const mendLJ = joint(legJ[1]!.abs, V(0.27, 0.72, 0.08), 'mendL');
  lump(mendLJ.abs, bloat, 0.08, 0.3, 0.68, 0.14, 1, 0.8, 0.6);

  // The trunk: a straining barrel of a belly in a split oilskin, the pale flesh bulging through the split.
  const hp = hipsJ.abs;
  lump(hp, oilD, 0.36, 0, 0.94, -0.02, 1.25, 0.6, 1.05);
  lump(ch, oil, 0.48, 0, 1.36, 0.06, 1.12, 1.05, 1.0, 14);
  lump(ch, bloat, 0.36, 0, 1.3, 0.3, 1.0, 1.0, 0.7, 14);
  // The split oilskin's edges, and its buttons long gone.
  for (const k of [-1, 1]) tube(ch, [V(k * 0.16, 1.7, 0.36), V(k * 0.3, 1.36, 0.5), V(k * 0.18, 1.02, 0.42)], 0.03, oilD, 6);
  const mendJ = joint(ch, V(0, 1.3, 0.48), 'mend');
  lump(mendJ.abs, bloat, 0.1, 0.08, 1.24, 0.5, 1, 0.9, 0.6);
  // A belt of rope with a stone tied on each side.
  const belt = M(new THREE.TorusGeometry(0.5, 0.025, 4, 18), rope, hp);
  belt.position.set(0, 1.04, 0.02);
  belt.rotation.x = Math.PI / 2;
  belt.scale.set(1.05, 0.9, 1);
  weight(hp, V(-0.42, 1.0, 0.2), 0.3, 0.12);
  weight(hp, V(0.4, 0.98, -0.1), 0.22, 0.1);

  // The nets over the shoulders, down the back, trailing on the ground behind it.
  const mn = maneJ.abs;
  netting(mn, V(-0.5, 1.9, -0.05), V(0.5, 1.92, -0.08), 0.9, 5);
  netting(mn, V(-0.42, 1.8, 0.24), V(-0.1, 1.86, 0.36), 0.5, 3);
  tube(mn, [V(0.2, 1.05, -0.3), V(0.3, 0.5, -0.5), V(0.36, 0.02, -0.85)], 0.012, net, 6);
  tube(mn, [V(-0.1, 1.0, -0.32), V(-0.22, 0.45, -0.55), V(-0.3, 0.02, -0.95)], 0.012, net, 6);
  for (let i = 0; i < 3; i++) M(new THREE.SphereGeometry(0.05, 6, 4), std('#c8b070', { roughness: 0.6 }), mn).position.set(-0.4 + i * 0.4, 1.92 + R() * 0.03, -0.1);
  // Weed hanging off the shoulders.
  for (let i = 0; i < 5; i++) {
    const x = -0.45 + i * 0.22;
    tube(mn, [V(x, 1.86, 0.1), V(x + R() * 0.05, 1.6, 0.16), V(x + R() * 0.08, 1.38, 0.12)], 0.02, weed, 4);
  }

  // The head: bloated and pale, sunk on the shoulders, ink-black eyes, a sodden cap, the mouth hanging open.
  const hd = headJ.abs;
  lump(hd, bloat, 0.26, 0, 1.84, 0.36, 1.05, 1, 1.0, 14);
  lump(hd, flesh, 0.14, -0.13, 1.76, 0.5, 1, 0.8, 0.8);
  lump(hd, flesh, 0.14, 0.13, 1.76, 0.5, 1, 0.8, 0.8);
  for (const k of [-1, 1]) {
    lump(hd, BLACK, 0.055, k * 0.09, 1.88, 0.58, 1, 0.8, 0.6, 8);
    glowSprite(hd, '#1a2a3a', 0.12, V(k * 0.09, 1.88, 0.62), 0.6);
  }
  // The cap: a sodden knit cap slumped to one side.
  const cap = lump(hd, oilD, 0.24, 0.04, 2.0, 0.32, 1.05, 0.55, 1.0, 12);
  cap.rotation.z = 0.25;
  lump(hd, oilD, 0.08, 0.2, 2.06, 0.22, 1, 1, 1, 8);
  const jw = jawJ.abs;
  lump(jw, fleshD, 0.13, 0, 1.66, 0.5, 1.1, 0.6, 0.9);
  lump(jw, BLACK, 0.07, 0, 1.7, 0.6, 1.2, 0.6, 0.5, 8);

  // Arms: thick and swollen, puffy hands with short fingers; a stone tied at one wrist.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const up = armJ[i]!.abs;
    const fo = foreJ[i]!.abs;
    const hn = handJ[i]!.abs;
    lump(up, oil, 0.2, SH[i]!.x, SH[i]!.y, SH[i]!.z, 1, 0.9, 1);
    limb(up, SH[i]!, EL[i]!, 0.17, 0.14, oil, 9);
    limb(fo, EL[i]!, WR[i]!, 0.14, 0.12, flesh, 9);
    lump(hn, bloat, 0.13, WR[i]!.x, WR[i]!.y - 0.06, WR[i]!.z + 0.04, 1, 1.1, 1);
    for (let f = 0; f < 4; f++) {
      const x = WR[i]!.x + (f - 1.5) * 0.05 * k;
      limb(hn, V(x, WR[i]!.y - 0.1, WR[i]!.z + 0.08), V(x, WR[i]!.y - 0.24, WR[i]!.z + 0.14), 0.035, 0.03, flesh, 6);
    }
    // A torn strand of net clutched in the hand.
    tube(hn, [V(WR[i]!.x, WR[i]!.y - 0.2, WR[i]!.z + 0.1), V(WR[i]!.x + k * 0.05, WR[i]!.y - 0.42, WR[i]!.z + 0.05), V(WR[i]!.x + k * 0.1, WR[i]!.y - 0.6, WR[i]!.z - 0.05)], 0.01, net, 4);
  }
  weight(handJ[1]!.abs, V(0.62, 0.66, 0.36), 0.18, 0.09);
  const mendAJ = joint(armJ[0]!.abs, V(-0.54, 1.38, 0.3), 'mendA');
  lump(mendAJ.abs, bloat, 0.07, -0.56, 1.4, 0.36, 1, 0.8, 0.7);

  // Dripping: a puddle where it stands, and drops hanging under the belly and the hands.
  const pool = M(new THREE.CircleGeometry(0.6, 16), water, g);
  pool.rotation.x = -Math.PI / 2;
  pool.position.set(0, 0.01, 0.04);
  const drops: THREE.Mesh[] = [];
  for (const p of [V(0, 0.86, 0.46), V(-0.6, 0.38, 0.46), V(0.6, 0.38, 0.46), V(0.2, 0.9, -0.3)]) {
    const d = M(new THREE.SphereGeometry(0.03, 6, 4), water, g);
    d.position.copy(p);
    d.scale.set(1, 1.6, 1);
    drops.push(d);
  }
  const t = (n: number): void => {
    drops.forEach((d, i) => {
      d.scale.y = 1.4 + 0.4 * (((n + i) % 4) / 3);
    });
  };
  t(0);
  return done(c, 2.15, t, undefined, bonesOf(c, {
    hips: hipsJ.p, chest: chestJ.p, head: headJ.p, jaw: jawJ.p, mane: maneJ.p,
    armL: armJ[0]!.p, foreL: foreJ[0]!.p, handL: handJ[0]!.p, armR: armJ[1]!.p, foreR: foreJ[1]!.p, handR: handJ[1]!.p,
    legL: legJ[0]!.p, shinL: shinJ[0]!.p, legR: legJ[1]!.p, shinR: shinJ[1]!.p,
    mend: mendJ.p, mendA: mendAJ.p, mendL: mendLJ.p,
  }));
};
